/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * RowEditActions — the shared commit/discard pair for an expanded editor row.
 *
 * Every mapping surface (request parameters, ORTB body, response mapping) opens
 * a row into a larger editor, and each had invented its own way out: "Apply
 * changes / Cancel edits" as filled buttons, a bare "Close", a bare "Done".
 * Only one of those told the operator what leaving would do, and only one
 * offered a way back. This component is the single answer: **Apply** commits and
 * closes, **Cancel** restores the row to what it was when the editor opened.
 *
 * Rendered as compact text actions so it sits inside a row bar rather than
 * dominating it — the tighter treatment the response mapper established.
 *
 * Surfaces that edit live (ORTB body, response mapping) snapshot the row on open
 * and restore that snapshot on Cancel; surfaces that buffer a draft (parameter
 * editor) discard the draft. Either way the contract the operator sees is the
 * same.
 */

import React from 'react';
import { Button, HStack } from '@chakra-ui/react';

export interface RowEditActionsProps {
  /** Commit the row and close the editor. */
  onApply: () => void;
  /** Restore the row to its pre-edit state and close the editor. */
  onCancel: () => void;
  /** Disables Apply — e.g. the row is mid-way through an invalid value. */
  applyDisabled?: boolean;
}

export const RowEditActions: React.FC<RowEditActionsProps> = ({ onApply, onCancel, applyDisabled }) => (
  <HStack gap={1}>
    <Button size="xs" variant="ghost" color="fg.muted" onClick={onCancel} _hover={{ color: 'fg.default' }}>
      Cancel
    </Button>
    <Button
      size="xs"
      variant="ghost"
      color="accent.solid"
      fontWeight="600"
      onClick={onApply}
      disabled={applyDisabled}
      _hover={{ bg: 'accent.subtle' }}
    >
      Apply
    </Button>
  </HStack>
);
